import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram, Linkedin, Mail, Facebook } from "lucide-react";

const social = [
  {
    href: "https://www.facebook.com/share/1Znv4Qgm5Z/?mibextid=wwXIfr",
    label: "Facebook",
    icon: Facebook,
  },
  {
    href: "https://www.instagram.com/dounia_elmoutii_properties_?igsh=dWtudnlreHlqYXdh",
    label: "Instagram",
    icon: Instagram,
  },
  {
    href: "https://www.linkedin.com/in/dounia-el-moutii-0423b79b/",
    label: "LinkedIn",
    icon: Linkedin,
  },
] as const;

const Footer = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#100e0c] px-6 pb-8 pt-20 text-[#F8F5EF] sm:px-8 sm:pt-24 lg:px-12 lg:pt-28">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:gap-10 lg:gap-16">
          <div className="md:col-span-5">
            <Link to="/" className="inline-block" aria-label="DEM Properties">
              <img src="/logo.webp" alt="DEM Properties" className="h-auto w-36 sm:w-40" width={208} height={90} />
            </Link>
            <p className="mt-8 max-w-sm font-playfair text-lg leading-relaxed text-stone-300 sm:text-xl">
              {t("footer.vision")}
            </p>
            <div className="mt-8 flex items-center gap-6 text-stone-400">
              {social.map(({ href, label, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="transition-colors hover:text-[#c89276]"
                >
                  <Icon size={18} strokeWidth={1.5} />
                </a>
              ))}
              <a
                href="mailto:d.elmoutii@demproperties.es"
                aria-label="Email"
                className="transition-colors hover:text-[#c89276]"
              >
                <Mail size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <h2 className="font-oswald text-[11px] uppercase tracking-[0.34em] text-[#c89276]">
              {t("footer.menu.title")}
            </h2>
            <nav className="mt-6 flex flex-col gap-3 font-oswald text-sm uppercase tracking-[0.16em] text-stone-200">
              <Link to="/" className="w-fit transition-colors hover:text-[#c89276]">{t("nav.home")}</Link>
              <Link to="/properties" className="w-fit transition-colors hover:text-[#c89276]">{t("nav.properties")}</Link>
              <Link to="/about" className="w-fit transition-colors hover:text-[#c89276]">{t("nav.about")}</Link>
              <Link to="/contact" className="w-fit transition-colors hover:text-[#c89276]">{t("nav.contact")}</Link>
            </nav>
          </div>

          <div className="md:col-span-3">
            <h2 className="font-oswald text-[11px] uppercase tracking-[0.34em] text-[#c89276]">
              {t("nav.contact")}
            </h2>
            <div className="mt-6 space-y-3">
              <p className="font-playfair text-xl text-stone-100">Marbella, Costa del Sol</p>
              <a href="tel:+34655623860" className="block font-oswald text-sm tracking-[0.12em] text-stone-400 transition-colors hover:text-[#c89276]">
                +34 655 62 38 60
              </a>
              <a href="mailto:d.elmoutii@demproperties.es" className="block font-oswald text-sm tracking-[0.06em] text-stone-400 transition-colors hover:text-[#c89276]">
                d.elmoutii@demproperties.es
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-col gap-5 border-t border-white/10 pt-6 sm:mt-20 md:flex-row md:items-center md:justify-between">
          <p className="font-oswald text-[11px] uppercase tracking-[0.22em] text-stone-400">
            © {year} DEM Properties — {t("footer.rights")}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-3 font-oswald text-[11px] uppercase tracking-[0.18em] text-stone-400">
            <Link to="/legal-notice" className="transition-colors hover:text-[#c89276]">
              {t("footer.legal", "Aviso Legal")}
            </Link>
            <Link to="/terms-of-service" className="transition-colors hover:text-[#c89276]">
              {t("footer.terms")}
            </Link>
            <Link to="/privacy-policy" className="transition-colors hover:text-[#c89276]">
              {t("footer.privacy")}
            </Link>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))}
              className="uppercase transition-colors hover:text-[#c89276]"
            >
              {t("footer.cookies", "Cookies")}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
